<script setup>
import { storeToRefs } from 'pinia'
import { useForm } from 'vee-validate'
import { useAuthStore } from '~/stores/auth'
import { isEqualUnorderedArray } from '~/utils/assertions'

// Auth store
const authStore = useAuthStore()

const { userStatus } = storeToRefs(authStore)

const {
    updateUserStatus,
    updateProfileSuccessCallback,
    updateProfileErrorCallback,
    updateProfileOnSettledCallback,
    updateProfileOnMutateCallback
} = authStore

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Load user data
const bearerToken = useState('state_bearerToken')
const oid = useState('state_userOid')
const correlationId = useState('state_appCorrelationId')

// Get user data
const { data: user, isLoading: isUserLoading, isError: isUserError } = useFetchProfile({ bearerToken, oid, correlationId })
// const { data: user, suspense: userSuspense, isError: isUserError } = useFetchProfile({ bearerToken, oid, correlationId })

/*
onServerPrefetch(async () => {
    await userSuspense()
})
*/

// Handles error or failed server status code  response
if (isUserError.value || user.value?.status === 'failed') {
    updateUserStatus({
        success: false,
        message: staticKeys.value?.account_form_error_load,
    })
}

// Compute current user
const currentUser = computed(() => user.value?.data)

// Prepare form //
const { values, meta, handleSubmit, isSubmitting } = useForm({
    preferences: ''
})

// Add dynamic values
const getPreferences = computed(() => currentUser.value?.preferences?.interests || [])

const isVisible = computed(() => {
    // User has not yet entered anything
    if (Object.keys(values).length === 0) {
        return false
    }

    // User has entered different data
    const useHasEnteredDifferentData = !isEqualUnorderedArray(getPreferences.value, Object.values(values).flat())

    // User has entered no data
    const useHasEnteredNoData = values.preferences?.length > 0

    // Show button at least success message is visible
    return (useHasEnteredDifferentData && meta.value?.valid && useHasEnteredNoData) || userStatus.value?.success
})

// Fetch preferences
const { data: preferences, suspense: preferenceSuspense } = useFetchConsumerPreferences()

// Prefetch data on the server side
onServerPrefetch(async () => {
    await preferenceSuspense() // Wait for the query to finish fetching
})

// Define config for form elements
const userPreferences = computed(() => {
    return preferences.value?.preferences?.map(obj => ({
        value: obj.id,
        smart: true,
        id: `preferences-[${obj.id}]`,
        label: obj.name,
        name: 'preferences'
    }))
})

const isFormSubmitting = computed(() => isSubmitting.value || userStatus.value?.success)

const submitButtonConfig = computed(() => {
    return {
        visible: isVisible.value,
        label: staticKeys.value?.account_form_button_label_savechanges,
        buttonConfig: {
            type: 'submit',
            buttonType: 'primary',
            isPending: isFormSubmitting.value || userIsPending.value,
            active: true
        }
    }
})

const getFormClass = computed(() => {
    return {
        'is-submitting': isFormSubmitting.value
    }
})

// Fetch mutation
const { mutate, isPending: userIsPending } = useUpdateProfileMutation({
    bearerToken,
    oid,
    onSuccessCallback: () => updateProfileSuccessCallback({ successMessage: staticKeys.value?.account_form_settings_success_message }),
    onErrorCallback: () => updateProfileErrorCallback({ errorMessage: staticKeys.value?.account_form_update_error, oid }),
    onMutateCallback: updateProfileOnMutateCallback,
    onSettledCallback: () => updateProfileOnSettledCallback({ oid }),
})

/**
 *  Handles form submit
 */
const onSubmit = handleSubmit(async (values) => {
    if (values) {
        const transformedUserValues = values.preferences?.length > 0
            ? {
                    preferences: {
                        interests: values.preferences
                    }
                }
            : {}

        // Fire update request
        mutate({
            body: transformedUserValues,
            oid
        })

        // Scroll to top sidebar if scroll container exists
        document.querySelector('.sidebar-content-inner').scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        })
    }
})

/**
 * Resets the status object with the given value.
 *
 * @param {any} value - The new value to set the status object to.
 * @return {void} This function does not return anything.
 */
const resetStatusObj = (value) => {
    updateUserStatus(value)
}
</script>

<template>
    <SideBarWrapper>
        <FormMessage :form-message-config="userStatus" @update:set-status="resetStatusObj" />

        <SideBarHeader>
            <SideBarMainHeadline :sidebar-main-headline-config="{ title: staticKeys?.menu_preferences_markdown }" />
        </SideBarHeader>

        <form class="account-preferences-form" :class="getFormClass" @submit.prevent="onSubmit">
            <SideBarHeadline :sidebar-headline-config="{ title: staticKeys?.account_menu_preferences_headline }" />

            <FormCheckBoxGroup>
                <FormCheckBox
                    v-for="item in userPreferences"
                    :key="item.id"
                    :checkbox-config="{ ...item, ...{ val: getPreferences }, ...{ pending: isSubmitting || userIsPending || isUserLoading } }"
                />
            </FormCheckBoxGroup>

            <FormButton :form-button-config="submitButtonConfig" />
        </form>
    </SideBarWrapper>
</template>

<style lang="scss">
.account-preferences-form {
    &::after {
        content: '';
        transition: opacity 400ms ease;
        opacity: 0;
    }

    &.is-submitting {
        @include form-is-submitting;
    }

    .btn-primary {
        margin-block-start: 30px;
    }
}
</style>
